import { useReducedMotion } from 'framer-motion'
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'

/** The checks' curves: back pops (peaks at 1.08), ring and draw for the badge, out for fades. */
export const EASE = {
  back: [0.34, 1.5, 0.64, 1],
  ring: [0.25, 1, 0.5, 1],
  draw: [0.65, 0, 0.35, 1],
  out: [0.33, 1, 0.68, 1],
} as const

export const STEP_OUT_MS = 150
export const STEP_OUT_S = STEP_OUT_MS / 1000
export const FADE_S = 0.2
/** What comes in rises this far as it fades in. */
export const RISE_PX = 8
export const SLIDE_PX = 16
export const SLIDE_S = 0.25
export const POP_IN = { scale: 0.6, opacity: 0 } as const
export const POP = { duration: 0.2, ease: EASE.back } as const
/** Tones ease instead of cutting. Stock classes: an arbitrary ease-[...] never compiles here. */
export const TONE_EASE = 'transition-colors duration-300 ease-out'
/** A check that usually takes seconds says it is slow after this long. */
export const SLOW_AFTER_MS = 15_000

/**
 * For the element holding one step, keyed by `step`: a later `rank` slides in from the right, an
 * earlier one from the left, and the same rank or the first step after loading (`''`) fades. No
 * entrance on mount, or while `isCovered`: the cover plays its own.
 */
export const useStepSlide = (step: string, rank: number, isCovered = false) => {
  const isStatic = useReducedMotion()
  const [last, setLast] = useState({ step, rank })
  const [side, setSide] = useState<number | null>(null)
  if (step !== last.step) {
    setLast({ step, rank })
    setSide(isCovered ? null : last.step ? Math.sign(rank - last.rank) : 0)
  }
  return {
    initial: side === null ? (false as const) : { opacity: 0, x: isStatic ? 0 : side * SLIDE_PX },
    animate: { opacity: 1, x: 0 },
    transition: { duration: side ? SLIDE_S : FADE_S, ease: EASE.out },
  }
}

/** False on the first render, then true: what mounts later may play an entrance. */
export const useHasMounted = () => {
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
  }, [])
  return mounted.current
}

/**
 * How the screen after a success comes in, for one that plays its own entrance: `hidden` under the
 * badge, `enter` from the swap. Undefined at rest, and always with reduced motion.
 */
export type Entrance = 'hidden' | 'enter' | undefined
export const EntranceContext = createContext<Entrance>(undefined)
export const useEntrance = () => useContext(EntranceContext)

const MORPH_MS = 300

/**
 * When `key` changes, eases the element from its last height to its new one, so a centred dialog
 * resizes instead of jumping. Reduced motion keeps the jump. Returns the element's ref: a callback,
 * since a portalled dialog attaches after its first commit.
 */
export const useHeightMorph = <T extends HTMLElement>(key: unknown) => {
  const isStatic = useReducedMotion()
  const [element, setElement] = useState<T | null>(null)
  const height = useRef<number>()
  const last = useRef<{ key: unknown }>()
  const morph = useRef<Animation>()
  // The height last laid out, so a morph starts where the eye last saw it.
  useLayoutEffect(() => {
    if (!element) return undefined
    height.current = element.offsetHeight
    const observer = new ResizeObserver(() => {
      height.current = element.offsetHeight
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [element])
  useLayoutEffect(() => {
    const previous = last.current
    last.current = { key }
    const from = height.current
    if (!previous || Object.is(previous.key, key) || !element || from === undefined) return
    // A morph still running holds the old height: stop it before measuring the new one.
    if (morph.current) {
      morph.current.cancel()
      morph.current = undefined
      element.style.overflowY = ''
    }
    const to = element.offsetHeight
    height.current = to
    if (isStatic || Math.abs(to - from) < 1 || typeof element.animate !== 'function') return
    // Clipped while it eases, or the new content would spill past the dialog.
    // Only top and bottom: a step sliding in overhangs the sides.
    element.style.overflowY = 'clip'
    const current = element.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: MORPH_MS,
      easing: `cubic-bezier(${EASE.out.join(',')})`,
    })
    morph.current = current
    current.onfinish = () => {
      if (morph.current === current) element.style.overflowY = ''
    }
  })
  return setElement
}
